import React from "react";
import styles from "./FHIRPatient.module.scss";
import "./previewDiff.css";
import _ from "lodash";
import PatientName from "./PatientName";
import Identifier from "./Identifier";
import Address from "./Address";
import Gender from "./Gender";
import BirthDate from "./BirthDate";
import Telecom from "./Telecom";
import Deceased from "./Deceased";
import MultipleBirth from "./MultipleBirth";
import MaritalStatus from "./MaritalStatus";
import LastUpdated from "./LastUpdated";
import Coverage from "./Coverage";
import CoverageIMDM from "./CoverageIMDM";
import Race from "./Race";
import Ethnicity from "./Ethinicity";
import Disability from "./Disability";
import Religion from "./Religion";
import OrganDonor from "./OrganDonor";
import Language from "./Language";
import AlternateContact from "./AlternateContact";
import LivingArrangement from "./LivingArrangement";
import LinkageHistory from "./LinkageHistory";
import Icon from "../Icon";
import { checkReadOnlyUser } from "../../utils/checkReadOnlyUser";
import { isImdmClient } from "../../utils/isImdmClient";
import { useAuthState } from "../../components/AuthProvider";
import RiskInformation from "./RiskInformation";
import AdditionalInformation from "./AdditionalInformation";
import ExcludedValues from "./ExcludedValues";
import { comparePatients, hasDiff, getDiffInfo } from "./diffUtils";
import {
  patientIdentifiers,
  patientCoverages,
  removeAdditionalDemographics
} from "./Utils";

const emptyPatient = {
  identifier: [],
  name: [],
  telecom: [],
  gender: "",
  birthDate: "",
  address: [],
  meta: {
    lastUpdated: ""
  }
};

const FHIRDetails = ({
  patient,
  priorAggregate,
  primaryRecords,
  linkageHistory = [],
  loggedInUser,
  summary: isSummary = false,
  showLastUpdated = false,
  isAggregateInfo = false,
  isPreviewMode = false,
  oncRecordOverrideIconClick,
  onAggregateAuxDataUpdate,
  isNewQuarantine = false,
  isMultipleAttributeOverride = false,
  setAttributeOverrides,
  attributeOverrides = [],
  ...rest
}) => {
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const _patient = Object.assign({}, emptyPatient, patient);
  const isReadOnly = checkReadOnlyUser(user);
  const isImdm = isImdmClient(selectedClient);
  const _patientExtensions = _patient.extension ? _patient.extension : "";
  const [expandAdditionDetails, setExpandAdditionalDetails] = React.useState(
    false
  );
  const [expandExcludedValues, setExpandExcludedValues] = React.useState(false);
  
  // Calculate differences if in preview mode
  const diffMap = React.useMemo(() => {
    if (isPreviewMode && priorAggregate) {
      return comparePatients(priorAggregate, _patient);
    }
    return {};
  }, [isPreviewMode, priorAggregate, _patient]);

  const linkageHistoryDisplay = linkageHistory
    .sort((a, b) => {
      return a.eventTimestamp < b.eventTimestamp ? 1 : -1;
    })
    .slice(0, 10);

  const _patientIdentifiers = patientIdentifiers(_patient.identifier);
  const _patientCoverages = patientCoverages(_patient.identifier);

  /* const _patientIdentifiers = _patient.identifier.filter(i => !("type" in i)); */

  const name = (
    <PatientName
      className={styles.name}
      data={removeAdditionalDemographics(_patient.name)}
      summary={isSummary}
      isAggregateInfo={isAggregateInfo}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      isInvisible={isReadOnly}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
      hasDiff={hasDiff(diffMap, 'name')}
      diffInfo={getDiffInfo(diffMap, 'name')}
      isPreviewMode={isPreviewMode}
    />
  );

  const address = (
    <Address
      className={styles.address}
      data={removeAdditionalDemographics(_patient.address)}
      summary={isSummary}
      isAggregateInfo={isAggregateInfo}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      isInvisible={isReadOnly}
      isImdm={isImdm}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
      hasDiff={hasDiff(diffMap, 'address')}
      diffInfo={getDiffInfo(diffMap, 'address')}
      isPreviewMode={isPreviewMode}
    />
  );

  const telecom = (
    <Telecom
      className={styles.telecom}
      data={removeAdditionalDemographics(_patient.telecom)}
      summary={isSummary}
      isAggregateInfo={isAggregateInfo}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      isInvisible={isReadOnly}
      isImdm={isImdm}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
      hasDiff={hasDiff(diffMap, 'telecom')}
      diffInfo={getDiffInfo(diffMap, 'telecom')}
      isPreviewMode={isPreviewMode}
    />
  );

  const identifiers = (
    <Identifier
      className={styles.name}
      data={_patientIdentifiers}
      patientId={_patient.id}
      sourceCode={_patient.meta && _patient.meta.source}
      summary={isSummary}
      isAggregateInfo={isAggregateInfo}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      onAggregateAuxDataUpdate={onAggregateAuxDataUpdate}
      isInvisible={isReadOnly}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
      hasDiff={hasDiff(diffMap, 'identifier')}
      diffInfo={getDiffInfo(diffMap, 'identifier')}
      isPreviewMode={isPreviewMode}
    />
  );

  const linkage = (
    <LinkageHistory
      className={styles.linkage}
      data={linkageHistoryDisplay}
      summary={isSummary}
      isInvisible={isReadOnly}
      isAggregateInfo={isAggregateInfo}
      isImdm={isImdm}
    />
  );

  const maritalStatus = _patient.hasOwnProperty("maritalStatus") ? (
    <MaritalStatus data={_patient.maritalStatus} summary={isSummary} />
  ) : null;

  const language = _patient.hasOwnProperty("communication") ? (
    <Language data={_patient.communication} />
  ) : null;

  const alternateContacts = _patient.hasOwnProperty("contact") ? (
    <AlternateContact data={_patient.contact} />
  ) : null;

  let race,
    ethnicity,
    birthplace,
    disability,
    religion,
    organDonor,
    livingArrangement,
    eiiaRiskInformation = null;
  let deceasedOverrideExtension = [];
  let birthDateOverrideExtension = [];
  let genderOverrideExtension = [];
  let multipleBirthOverrideExtension = [];

  if (_patient.hasOwnProperty("extension")) {
    const birthplaceAddress = _patient.extension.filter(({ url }) =>
      url.includes("patient-birthPlace")
    );
    const raceData = _patient.extension.filter(({ url }) =>
      url.includes("us-core-race")
    );
    const ethnicityData = _patient.extension.filter(({ url }) =>
      url.includes("us-core-ethnicity")
    );
    const disabilityData = _patient.extension.filter(({ url }) =>
      url.includes("patient-disability")
    );
    const religionData = _patient.extension.filter(({ url }) =>
      url.includes("patient-religion")
    );
    const donorData = _patient.extension.filter(({ url }) =>
      url.includes("cadavericDonor")
    );
    const livingArrangementData = _patient.extension.filter(({ url }) =>
      url.includes("LivingArrangement")
    );
    const eiiaRiskInformationData = _patient.extension.filter(({ url }) =>
      url.includes("eiia-risk-score")
    );

    birthDateOverrideExtension = _patient.extension.filter(
      ({ url }) =>
        url ===
        "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#overrideBirthDate/"
    );

    genderOverrideExtension = _patient.extension.filter(
      ({ url }) =>
        url ===
        "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#overrideGender/"
    );

    multipleBirthOverrideExtension = _patient.extension.filter(
      ({ url }) =>
        url ===
        "https://github.optum.com/pages/HealthcareMatching/hcm-microsite/fhir-extensions/#overrideMultipleBirthIndicator/"
    );

    race = raceData.length ? <Race data={raceData} /> : null;

    ethnicity = ethnicityData.length ? (
      <Ethnicity data={ethnicityData} />
    ) : null;

    disability = disabilityData.length ? (
      <Disability data={disabilityData} />
    ) : null;

    religion = religionData.length ? <Religion data={religionData} /> : null;

    organDonor = donorData.length ? <OrganDonor data={donorData} /> : null;

    livingArrangement = livingArrangementData.length ? (
      <LivingArrangement data={livingArrangementData} />
    ) : null;

    birthplace = birthplaceAddress.length ? (
      <Address
        className={styles.address}
        type="birthplaceAddress"
        data={[birthplaceAddress[0].valueAddress]}
        oncRecordOverrideIconClick={oncRecordOverrideIconClick}
        isInvisible={isReadOnly}
        isAggregateInfo={isAggregateInfo}
      />
    ) : null;

    eiiaRiskInformation = eiiaRiskInformationData.length ? (
      <RiskInformation className={styles.name} data={eiiaRiskInformationData} />
    ) : null;
  }

  const gender = (
    <Gender
      data={_patient.gender}
      summary={isSummary}
      extensions={_patientExtensions}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      overrideExtension={genderOverrideExtension}
      isInvisible={isReadOnly}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
    />
  );
  const dob = (
    <BirthDate
      data={_patient.birthDate}
      summary={isSummary}
      extensions={_patientExtensions}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      overrideExtension={birthDateOverrideExtension}
      isInvisible={isReadOnly}
      isNewQuarantine={isNewQuarantine}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
      hasDiff={hasDiff(diffMap, 'birthDate')}
      diffInfo={getDiffInfo(diffMap, 'birthDate')}
      isPreviewMode={isPreviewMode}
    />
  );
  const multipleBirth = _patient.hasOwnProperty("multipleBirthInteger") ? (
    <MultipleBirth
      data={_patient.multipleBirthInteger}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      isInvisible={isReadOnly}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
    />
  ) : _patient.hasOwnProperty("multipleBirthBoolean") ? (
    <MultipleBirth
      data={_patient.multipleBirthBoolean}
      overrideExtension={multipleBirthOverrideExtension}
      oncRecordOverrideIconClick={oncRecordOverrideIconClick}
      isInvisible={isReadOnly}
      isMultipleAttributeOverride={isMultipleAttributeOverride}
      setAttributeOverrides={setAttributeOverrides}
      attributeOverrides={attributeOverrides}
    />
  ) : null;

  const deceased = _patient.hasOwnProperty("deceasedDateTime") ? (
    <Deceased
      data={_patient.deceasedDateTime}
      extensions={_patientExtensions}
    />
  ) : _patient.hasOwnProperty("deceasedBoolean") ? (
    <Deceased data={_patient.deceasedBoolean} extensions={_patientExtensions} />
  ) : null;

  let isImdmCoverage = false;
  if (_patientCoverages.length > 0) {
    try {
      const value =
        Object.keys(_patientCoverages[0]).length === 1 &&
        _patientCoverages[0].hasOwnProperty("extension")
          ? _patientCoverages[0].extension[0].valueIdentifier.value
          : _patientCoverages[0].value;
      let json = JSON.parse(value);
      isImdmCoverage = typeof json === "object";
    } catch (e) {
      isImdmCoverage = false;
    }
  }
  const coverage =
    _patientCoverages.length > 0 ? (
      !isImdmCoverage ? (
        <Coverage
          className={styles.name}
          data={_patientCoverages}
          summary={isSummary}
          isAggregateInfo={isAggregateInfo}
        />
      ) : (
        <CoverageIMDM
          className={styles.name}
          data={_patientCoverages}
          extension={_patient.extension}
          summary={isSummary}
          isAggregateInfo={isAggregateInfo}
          oncRecordOverrideIconClick={oncRecordOverrideIconClick}
          isInvisible={isReadOnly}
          isMultipleAttributeOverride={isMultipleAttributeOverride}
          setAttributeOverrides={setAttributeOverrides}
          attributeOverrides={attributeOverrides}
        />
      )
    ) : null;

  const lastUpdated = (
    <LastUpdated
      data={_patient.meta.lastUpdated}
      user={user}
      showLastUpdated={showLastUpdated}
    />
  );

  if (isSummary) {
    return (
      <div {...rest}>
        {name}
        {gender}
        {dob}
        {address}
        {coverage}
        {lastUpdated}
      </div>
    );
  }

  return (
    <div className={styles.base}>
      {name ? (
        <div className={styles.row}>
          <div className={styles.heading}>Name</div>
          <div className={styles.info}>{name}</div>
        </div>
      ) : null}

      {eiiaRiskInformation ? (
        <div className={styles.row}>
          <div className={styles.heading}>EIIA Risk Information</div>
          <div className={styles.info}>{eiiaRiskInformation}</div>
        </div>
      ) : null}

      {address ? (
        <div className={styles.row}>
          <div className={styles.heading}>Address</div>
          <div className={styles.info}>{address}</div>
        </div>
      ) : null}

      {birthplace ? (
        <div className={styles.row}>
          <div className={styles.heading}>Birth Place Address</div>
          <div className={styles.info}>{birthplace}</div>
        </div>
      ) : null}

      <div className={styles.row}>
        <div className={styles.heading}>Basic Demographics</div>
        <div className={styles.info}>
          <section>
            <div className={styles.basicDemogaraphic}>{gender}</div>
          </section>
          <section>
            <div className={styles.basicDemogaraphic}>{dob}</div>
          </section>
          <section>
            <div className={styles.basicDemogaraphic}>{deceased}</div>
          </section>
          <section>
            <div className={styles.basicDemogaraphic}>{multipleBirth}</div>
          </section>

          <section>
            <div>
              {maritalStatus}

              {livingArrangement}
            </div>
          </section>
        </div>
      </div>

      {telecom ? (
        <div className={styles.row}>
          <div className={styles.heading}>Contact Information</div>
          <div className={styles.info}>{telecom}</div>
        </div>
      ) : null}

      {alternateContacts ? (
        <div className={styles.row}>
          <div className={styles.heading}>Alternate Contact(s)</div>
          <div className={styles.info}>{alternateContacts}</div>
        </div>
      ) : null}

      {identifiers ? (
        <div className={styles.row}>
          <div className={styles.heading}>Identifiers</div>
          <div className={styles.info}>{identifiers}</div>
        </div>
      ) : null}

      {coverage ? (
        <div className={styles.row}>
          <div className={styles.heading}>Coverage</div>
          <div className={styles.info}>{coverage}</div>
        </div>
      ) : null}

      {linkage && isAggregateInfo ? ( // && isImdm
        <div className={styles.row}>
          <div className={styles.heading}>Linkage History</div>
          <div className={styles.info}>{linkage}</div>
        </div>
      ) : null}

      {(_patient.hasOwnProperty("extension") ||
        _patient.hasOwnProperty("communication")) && (
        <div className={styles.row}>
          <div className={styles.heading}>Additional Information</div>
          <div className={styles.info}>
            <section>
              <div>
                {race}
                {ethnicity}
                {disability}
              </div>
              <div>
                {religion}
                {organDonor}
                {language}
              </div>
            </section>
          </div>
        </div>
      )}

      {isAggregateInfo && (
        <div className={styles.row}>
          <div
            className={styles.heading}
            style={{ cursor: "pointer" }}
            onClick={() => setExpandAdditionalDetails(!expandAdditionDetails)}
          >
            Non Survived Attributes{" "}
            <span
              className={
                expandAdditionDetails
                  ? styles.iconCaretDown
                  : styles.iconCaretRight
              }
            />{" "}
          </div>
          {expandAdditionDetails && (
            <div className={styles.info}>
              <AdditionalInformation data={_patient} />
            </div>
          )}
        </div>
      )}

      {isAggregateInfo && (
        <div className={styles.row}>
          <div
            className={styles.heading}
            style={{ cursor: "pointer" }}
            onClick={() => setExpandExcludedValues(!expandExcludedValues)}
          >
            Excluded Values{" "}
            <span
              className={
                expandExcludedValues
                  ? styles.iconCaretDown
                  : styles.iconCaretRight
              }
            />{" "}
          </div>
          {expandExcludedValues && (
            <div className={styles.info}>
              <ExcludedValues
                primaryRecords={primaryRecords}
                isGoldenRecordExclusionEnabled={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FHIRDetails;
